import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Search, Filter, Download, Eye, CheckCircle, XCircle, Clock,
  DollarSign, Calendar, Mail, Phone, Building, Briefcase, MapPin,
  CreditCard, ChevronDown, ChevronUp, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import axios from 'axios';
import { format } from 'date-fns/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Registrations = () => {
  const [registrations, setRegistrations] = useState([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTraining, setSelectedTraining] = useState('all');
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState('all');
  const [selectedRegistrationStatus, setSelectedRegistrationStatus] = useState('all');
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterRegistrations();
  }, [registrations, searchQuery, selectedTraining, selectedPaymentStatus, selectedRegistrationStatus]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const [registrationsRes, trainingsRes] = await Promise.all([
        axios.get(`${API}/admin/registrations`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/training-calendar`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setRegistrations(registrationsRes.data);
      setTrainings(trainingsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterRegistrations = () => {
    let filtered = [...registrations];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(reg =>
        reg.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        reg.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        reg.phone.includes(searchQuery) ||
        reg.organization?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Training filter
    if (selectedTraining !== 'all') {
      filtered = filtered.filter(reg => reg.training_id === parseInt(selectedTraining));
    }

    // Payment status filter
    if (selectedPaymentStatus !== 'all') {
      filtered = filtered.filter(reg => reg.payment_status === selectedPaymentStatus);
    }

    // Registration status filter
    if (selectedRegistrationStatus !== 'all') {
      filtered = filtered.filter(reg => reg.registration_status === selectedRegistrationStatus);
    }

    setFilteredRegistrations(filtered);
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      initiated: { color: 'bg-blue-100 text-blue-800', label: 'Initiated' },
      completed: { color: 'bg-green-100 text-green-800', label: 'Completed' },
      failed: { color: 'bg-red-100 text-red-800', label: 'Failed' },
      refunded: { color: 'bg-gray-100 text-gray-800', label: 'Refunded' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getRegistrationStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      confirmed: { color: 'bg-green-100 text-green-800', label: 'Confirmed' },
      cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  };

  const formatAmount = (amount) => {
    if (!amount) return 'N/A';
    return `₹${(amount / 100).toLocaleString('en-IN')}`;
  };

  const exportToCSV = () => {
    const headers = [
      'Registration ID', 'Training', 'Full Name', 'Email', 'Phone',
      'Organization', 'Designation', 'City', 'Registration Status',
      'Payment Status', 'Payment Amount', 'Registered At'
    ];

    const rows = filteredRegistrations.map(reg => [
      reg.id,
      trainings.find(t => t.id === reg.training_id)?.title || 'N/A',
      reg.full_name,
      reg.email,
      reg.phone,
      reg.organization || 'N/A',
      reg.designation || 'N/A',
      reg.city || 'N/A',
      reg.registration_status,
      reg.payment_status,
      reg.payment_amount ? formatAmount(reg.payment_amount) : 'N/A',
      formatDate(reg.registered_at)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `registrations_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const toggleRowExpansion = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const stats = {
    total: filteredRegistrations.length,
    confirmed: filteredRegistrations.filter(r => r.registration_status === 'confirmed').length,
    pending: filteredRegistrations.filter(r => r.registration_status === 'pending').length,
    paid: filteredRegistrations.filter(r => r.payment_status === 'completed').length
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Training Registrations</h1>
          <p className="text-gray-600 mt-1">Manage and view all training registrations</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Button onClick={exportToCSV} className="gap-2 bg-green-600 hover:bg-green-700">
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Registrations</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <Users className="h-12 w-12 text-blue-600 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Confirmed</p>
                <p className="text-3xl font-bold text-green-600">{stats.confirmed}</p>
              </div>
              <CheckCircle className="h-12 w-12 text-green-600 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending</p>
                <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <Clock className="h-12 w-12 text-yellow-600 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Payments Completed</p>
                <p className="text-3xl font-bold text-blue-600">{stats.paid}</p>
              </div>
              <DollarSign className="h-12 w-12 text-blue-600 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by name, email, phone, organization..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Training Filter */}
            <select
              value={selectedTraining}
              onChange={(e) => setSelectedTraining(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Trainings</option>
              {trainings.map(training => (
                <option key={training.id} value={training.id}>{training.title}</option>
              ))}
            </select>

            {/* Payment Status Filter */}
            <select
              value={selectedPaymentStatus}
              onChange={(e) => setSelectedPaymentStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Payment Status</option>
              <option value="pending">Pending</option>
              <option value="initiated">Initiated</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>

            {/* Registration Status Filter */}
            <select
              value={selectedRegistrationStatus}
              onChange={(e) => setSelectedRegistrationStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Reg. Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Results Count */}
      <div className="text-sm text-gray-600">
        Showing <span className="font-semibold">{filteredRegistrations.length}</span> of <span className="font-semibold">{registrations.length}</span> registrations
      </div>

      {/* Registrations Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Participant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Training
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Registration Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Registered
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRegistrations.map((registration) => {
                  const training = trainings.find(t => t.id === registration.training_id);
                  const isExpanded = expandedRows.has(registration.id);

                  return (
                    <React.Fragment key={registration.id}>
                      <tr className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          #{registration.id}
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{registration.full_name}</div>
                            <div className="text-sm text-gray-500">{registration.email}</div>
                            <div className="text-sm text-gray-500">{registration.phone}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-xs truncate">
                            {training?.title || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{registration.organization || 'N/A'}</div>
                          <div className="text-sm text-gray-500">{registration.designation || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getRegistrationStatusBadge(registration.registration_status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getPaymentStatusBadge(registration.payment_status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(registration.registered_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => toggleRowExpansion(registration.id)}
                              className="gap-1"
                            >
                              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                              {isExpanded ? 'Less' : 'More'}
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedRegistration(registration);
                                setIsDetailOpen(true);
                              }}
                              className="gap-1"
                            >
                              <Eye className="h-4 w-4" />
                              View
                            </Button>
                          </div>
                        </td>
                      </tr>

                      {/* Expanded Row */}
                      {isExpanded && (
                        <tr className="bg-gray-50">
                          <td colSpan="8" className="px-6 py-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="font-medium text-gray-700">City:</span>
                                <span className="ml-2 text-gray-900">{registration.city || 'N/A'}</span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">Experience:</span>
                                <span className="ml-2 text-gray-900">{registration.experience_years ? `${registration.experience_years} years` : 'N/A'}</span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">How did you hear:</span>
                                <span className="ml-2 text-gray-900">{registration.how_did_you_hear || 'N/A'}</span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">Payment Amount:</span>
                                <span className="ml-2 text-gray-900 font-semibold">{registration.payment_amount ? formatAmount(registration.payment_amount) : 'N/A'}</span>
                              </div>
                              {registration.payment_method && (
                                <div>
                                  <span className="font-medium text-gray-700">Payment Method:</span>
                                  <span className="ml-2 text-gray-900 uppercase">{registration.payment_method}</span>
                                </div>
                              )}
                              {registration.razorpay_payment_id && (
                                <div>
                                  <span className="font-medium text-gray-700">Payment ID:</span>
                                  <span className="ml-2 text-gray-900 font-mono text-xs">{registration.razorpay_payment_id}</span>
                                </div>
                              )}
                              {registration.special_requirements && (
                                <div className="col-span-2">
                                  <span className="font-medium text-gray-700">Special Requirements:</span>
                                  <p className="mt-1 text-gray-900">{registration.special_requirements}</p>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}

                {filteredRegistrations.length === 0 && (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                      <Users className="h-12 w-12 mx-auto mb-4 opacity-20" />
                      <p>No registrations found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Registration Details #{selectedRegistration?.id}</DialogTitle>
          </DialogHeader>

          {selectedRegistration && (
            <div className="space-y-6">
              {/* Participant Information */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Participant Information
                </h3>
                <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Full Name</label>
                    <p className="text-gray-900">{selectedRegistration.full_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Email</label>
                    <p className="text-gray-900">{selectedRegistration.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Phone</label>
                    <p className="text-gray-900">{selectedRegistration.phone}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">City</label>
                    <p className="text-gray-900">{selectedRegistration.city || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Organization Information */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Organization Information
                </h3>
                <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Organization</label>
                    <p className="text-gray-900">{selectedRegistration.organization || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Designation</label>
                    <p className="text-gray-900">{selectedRegistration.designation || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Experience</label>
                    <p className="text-gray-900">{selectedRegistration.experience_years ? `${selectedRegistration.experience_years} years` : 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">How did you hear</label>
                    <p className="text-gray-900">{selectedRegistration.how_did_you_hear || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Training Information */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Training Information
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-medium text-gray-900">
                    {trainings.find(t => t.id === selectedRegistration.training_id)?.title || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Payment Information */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Payment Information
                </h3>
                <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Payment Status</label>
                    <div className="mt-1">{getPaymentStatusBadge(selectedRegistration.payment_status)}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Registration Status</label>
                    <div className="mt-1">{getRegistrationStatusBadge(selectedRegistration.registration_status)}</div>
                  </div>
                  {selectedRegistration.payment_amount && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">Amount</label>
                      <p className="text-lg font-bold text-blue-600">{formatAmount(selectedRegistration.payment_amount)}</p>
                    </div>
                  )}
                  {selectedRegistration.payment_method && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">Payment Method</label>
                      <p className="text-gray-900 uppercase">{selectedRegistration.payment_method}</p>
                    </div>
                  )}
                  {selectedRegistration.razorpay_order_id && (
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-gray-600">Order ID</label>
                      <p className="text-gray-900 font-mono text-sm">{selectedRegistration.razorpay_order_id}</p>
                    </div>
                  )}
                  {selectedRegistration.razorpay_payment_id && (
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-gray-600">Payment ID</label>
                      <p className="text-gray-900 font-mono text-sm">{selectedRegistration.razorpay_payment_id}</p>
                    </div>
                  )}
                  {selectedRegistration.payment_completed_at && (
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-gray-600">Payment Completed At</label>
                      <p className="text-gray-900">{formatDate(selectedRegistration.payment_completed_at)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Special Requirements */}
              {selectedRegistration.special_requirements && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">Special Requirements</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-900">{selectedRegistration.special_requirements}</p>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 border-t pt-4">
                <div>
                  <span className="font-medium">Registered At:</span>
                  <span className="ml-2">{formatDate(selectedRegistration.registered_at)}</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
